import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { searchSmLine } from "@tql/ui-components-react/untitled/icons";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useMemo, useRef, useState } from "react";

import { routes } from "~/app/routes/web-routes.ts";
import { useSetActiveCustomerProfile } from "~/features/profile/hooks/use-set-active-customer-profile.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";
import {
	availableCustomersAtom,
	selectedCustomerAtom,
} from "~/shared/state/user-profile-state.ts";
import { markLoginEventPending } from "~/shared/utils/login-event-session.ts";

import { TraxModal } from "../trax-modal/trax-modal.tsx";
import styles from "./profile-switcher.module.css";

type ProfileSwitcherProps = {
	readonly onClose: () => void;
};

export function ProfileSwitcher({ onClose }: ProfileSwitcherProps) {
	const [searchTerm, setSearchTerm] = useState("");
	const [pendingCustomer, setPendingCustomer] =
		useState<CustomerDetails | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const availableCustomers = useAtomValue(availableCustomersAtom);
	const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
	const { mutateAsync: saveActiveProfile, isPending: isSaving } =
		useSetActiveCustomerProfile();
	// We only consume `isLoading` here to gate the confirm button. The PUT URL
	// is computed inside `useSetActiveCustomerProfile` from the current role,
	// so we don't need to reference `isVendor` directly.
	const { isLoading: isRoleLoading } = useIsVendor();
	const panelRef = useRef<HTMLDivElement>(null);

	useAutoSpinner(isSaving);

	const handleCustomerClick = useCallback(
		(customer: CustomerDetails) => {
			// Block selection until the user's role for the currently-active
			// customer has resolved. The PUT URL is role-dependent, and
			// `useIsVendor` defaults to vendor while loading \u2014 so firing during
			// that window can route to the wrong endpoint and 403. Also guards
			// the `skipPreference` auto-confirm path, which would otherwise
			// PUT immediately on mount of the (suppressed) modal.
			if (isRoleLoading) return;
			if (isSaving) return;
			if (customer.id === selectedCustomer?.id) return;
			setPendingCustomer(customer);
		},
		[selectedCustomer?.id, isRoleLoading, isSaving],
	);

	const handleConfirm = useCallback(() => {
		if (!pendingCustomer || isSaving) return;
		const target = pendingCustomer;
		// Clear immediately so a double-click on the confirm button \u2014 or
		// `TraxModal`'s `skipPreference` auto-confirm effect double-firing in
		// dev StrictMode \u2014 can't queue a second PUT before the first resolves.
		setPendingCustomer(null);

		// Fire the PUT under the *current* customer's role. The URL inside
		// `useSetActiveCustomerProfile` is computed from `useIsVendor()` at the
		// most recent render \u2014 i.e. the role the user is currently signed in
		// as \u2014 which matches the customer header the request will carry. This
		// is the variant the BFF accepts, so the call succeeds whether the
		// target is a vendor or admin relationship.
		//
		// `useGetCurrentUserInfo` is keyed per customer, so the role is correct
		// for whichever customer is currently selected (no stale role from a
		// previously-active customer).
		void saveActiveProfile(target.id)
			.then(() => {
				// Commit the atom only after the persisted profile is saved so a
				// half-applied state can never linger. We always reload to the
				// root and let the route guards / landing logic redirect based
				// on the *target* customer's freshly-loaded role; this avoids
				// guessing a destination that may not exist for same-role swaps
				// (e.g. vendor \u2192 vendor would otherwise land on `/track`).
				//
				// `selectedCustomerAtom` hydrates from this sessionStorage key
				// on module load (see `user-profile-state.ts`). Writing it here
				// is what makes the customer headers (`Tql-Identity-Companyid` /
				// `Tql-Identity-Accounttype`) available on the *very first*
				// request after the reload \u2014 without it, boot-time calls fire
				// header-less and the BFF 403s. The in-memory atom set is for
				// the rest of this render before navigation.
				try {
					globalThis.sessionStorage.setItem(
						"selectedCustomers",
						JSON.stringify(target),
					);
				} catch {
					// sessionStorage can throw in private modes; the BFF GET will
					// still restore the persisted profile after reload, just not
					// in time for request #1.
				}
				setSelectedCustomer(target);
				// Switching to a new customer profile is a deliberate re-entry into
				// that customer's context, so record a login event after the reload
				// (the marker survives the navigation; a passive refresh sets none).
				markLoginEventPending();
				onClose();
				globalThis.location.assign(routes.root);
			})
			.catch(() => {
				setShowErrorModal(true);
			});
	}, [
		pendingCustomer,
		isSaving,
		saveActiveProfile,
		setSelectedCustomer,
		onClose,
	]);

	const handleCancel = useCallback(() => {
		if (isSaving) return;
		setPendingCustomer(null);
	}, [isSaving]);

	const filteredCustomers = useMemo(() => {
		const term = searchTerm.toLowerCase();
		const matches = term
			? availableCustomers.filter((customer) =>
					customer.name.toLowerCase().includes(term),
				)
			: availableCustomers;

		if (!selectedCustomer) return matches;
		const selectedIndex = matches.findIndex(
			(customer) => customer.id === selectedCustomer.id,
		);
		if (selectedIndex <= 0) return matches;
		const reordered = [...matches];
		const [selected] = reordered.splice(selectedIndex, 1);
		if (selected) reordered.unshift(selected);
		return reordered;
	}, [searchTerm, availableCustomers, selectedCustomer]);

	return (
		<div
			ref={panelRef}
			className={styles.profileSearchCard}
			data-testid={"profile-switcher"}
		>
			<div className={styles.searchCard}>
				<div className={styles.searchInputWrapper}>
					<input
						className={styles.searchInput}
						data-testid={"profile-search-input"}
						onChange={(e) => setSearchTerm(e.target.value)}
						placeholder={"Search profiles..."}
						type={"text"}
						value={searchTerm}
					/>
					<SvgIcon className={styles.searchIcon} icon={searchSmLine} />
				</div>
				<ul className={styles.customerList} data-testid={"customer-list"}>
					{filteredCustomers.length > 0 ? (
						filteredCustomers.map((customer) => {
							const itemClass =
								customer.id === selectedCustomer?.id
									? `${styles.customerListItem} ${styles.customerListItemSelected}`
									: styles.customerListItem;
							return (
								<li
									key={customer.id}
									aria-selected={customer.id === selectedCustomer?.id}
									className={itemClass}
									data-testid={"customer-list-item"}
									onClick={() => handleCustomerClick(customer)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											handleCustomerClick(customer);
										}
									}}
									role={"option"}
									tabIndex={0}
								>
									<div>
										<div className={styles.customerName}>{customer.name}</div>
									</div>
									{customer.id === selectedCustomer?.id ? (
										<span className={styles.activeBadge}>Active</span>
									) : null}
								</li>
							);
						})
					) : (
						<li className={styles.emptyMessage}>No matching customers</li>
					)}
				</ul>
			</div>

			{pendingCustomer ? (
				<TraxModal
					showConfirmationButtons
					confirmText={"I understand."}
					onCancel={handleCancel}
					onClose={handleCancel}
					onConfirm={handleConfirm}
					showButtonsDisabled={isRoleLoading || isSaving}
					skipPreference={{
						label: "Don't show this message again.",
						storageKey: "profile-switcher-skip",
					}}
					title={"Change Active Profile?"}
					type={"confirmation"}
				>
					<div>If you change your Active Profile you will be redirected.</div>
				</TraxModal>
			) : null}

			{showErrorModal ? (
				<TraxModal
					showConfirmationButtons
					confirmText={"Close"}
					onClose={() => setShowErrorModal(false)}
					onConfirm={() => setShowErrorModal(false)}
					showCancelButton={false}
					title={"Unable to Change Profile"}
					type={"error"}
				>
					<p>
						We were unable to update your active customer profile. Please try
						again.
					</p>
				</TraxModal>
			) : null}
		</div>
	);
}
